import { FC, useEffect, useMemo, useRef, useState } from "react";
import { SubstanceCreatorModal } from ".";
import styles from './SubstanceGrid.module.scss'
import { LoadingStatus } from "../../../@types/enumsGlobal";
import DefaultDictionaryCommandsPanel from "../../../components/commandsPanels/DefaultDictionaryCommandsPanel";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import { ISubstanceViewDTO } from "../../../libs/coreapi-dto/dirs/substance";
import { SubstanceDataProvider } from "../../../Services/DataProviders/SubstanceDataProvider";
import useGridFilter, { CreateSelectorGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { checkAccessStatus } from "../../CommonHelperFunctions";
import GridWrapper from "../../../components/controls/GridWrapper";
import { Input } from "antd";
import { SearchIcon } from "../../../libs/corporate/icons/outlined/action/MainCollection";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { useGridFilterValidation } from "../../../system/hooks/useGridFilterValidation";

const MIN_SEARCH_LENGTH = 3;

const SubstanceGrid: FC<IGridProps> = (props) => {
    const appCtx = useAppContext();
    const userContext = useUserContext();
    const substanceDataProvider = new SubstanceDataProvider(appCtx.coreApiService)

    const [data, setData] = useState<ISubstanceViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter(props.baseGridFilter ?? CreateSelectorGridFilter);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [searchValue, setSearchValue] = useState<string>('');
    const isRealRefresh = useRef(false);
    const { hasFilter } = useGridFilterValidation(gridFilter);
    
    const { t } = useTranslation();

    const handleItemsChange = (newItems: IGridRow[]) => {
        setSelectedItems(newItems);
        props?.onMultipleSelect?.(newItems);
    };
   
    useEffect(() => {
        setIsSubmitting(true);

        if (hasFilter && props.multipleSelect && props.isSearchForMultyselect) {                       
            substanceDataProvider.getView(gridFilter, (_, totalCount) => {
                if (totalCount > gridFilter.numberPerPage) {
                    const allDataFilter = { ...gridFilter, numberPerPage: totalCount, pageNumber: 1 };
                    substanceDataProvider.getView(allDataFilter, (data, total) => {
                        setData(data);
                        setTotalCount(total);
                        data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData);
                        setIsSubmitting(false);
                    });
                } else {
                    substanceDataProvider.getView(gridFilter, (data, total) => {
                        setData(data);
                        setTotalCount(total);
                        data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData);
                        setIsSubmitting(false);
                    });
                }
            });
        } else {            
            substanceDataProvider.getView(gridFilter, (data, totalCount) => {
                setData(data);
                setTotalCount(totalCount);
                data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData);
                setIsSubmitting(false);
            });
        }
    }, [gridFilter]);

    useEffect(() => {
        if (viewState === 'refresh') {
            setIsSubmitting(true)
            substanceDataProvider.getView(gridFilter, (data, totalCount) => {
                setData(data);
                setTotalCount(totalCount)
                setViewState('view')
                data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData)
                setIsSubmitting(false)
            })
        }
    }, [viewState])

    useEffect(() => {
        if (props.dataFromPluginGridSelectorInput) {
            setSelectedItems(props.dataFromPluginGridSelectorInput);
        }
    }, [props.dataFromPluginGridSelectorInput]);
    
    const searchSubstance = (value: string) => {
            if (value.length >= MIN_SEARCH_LENGTH) {
                const expression = `lower("Name") like lower('%${value}%') or lower("NameLat") like lower('%${value}%')`;
                dispatchGridFilter({ type: "addCustomWhereExpressionFilter", payload: expression });
            } else if (value.length === 0) {
                dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
            }
        };
    
        const debouncedSearch = useMemo(
            () => debounce(searchSubstance, 1250),
            []
        );
    
        const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setSearchValue(value);
            
            if (props.isModal) {
                props.onFocusChanged?.(value.length > 0);
            }
            
            if (value.length === 0) {
                debouncedSearch.cancel();
                dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
            }
        };
    
        const handlePressEnter = () => {
            debouncedSearch.cancel();
            if (searchValue.length >= MIN_SEARCH_LENGTH || searchValue.length === 0) {
                searchSubstance(searchValue);
            }
        };
    
        useEffect(() => {
            if (searchValue !== '' && searchValue.length >= MIN_SEARCH_LENGTH) {
                debouncedSearch(searchValue);
            }
        }, [searchValue]);
    
        useEffect(() => {
            return () => debouncedSearch.cancel();
        }, [debouncedSearch]);
       
        useEffect(() => {
            if (viewState === 'refresh' && isRealRefresh.current) {
                isRealRefresh.current = false;
                setSearchValue("");
                dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
            }
            setViewState('view');
        }, [viewState]);
    

    return (
        <PluginWrapper>
            {
                checkAccessStatus(props.plugin.permission as IPermission, userContext.userPermission) && 
                <>
                    <ToolbarWrapper className={styles.toolBarWrapper}>
                        <DefaultDictionaryCommandsPanel
                            selectedItem={[selectedItem, setSelectedItem]}
                            gridFilter={[gridFilter, dispatchGridFilter]}
                            dataProvider={substanceDataProvider}
                            creatorModalJsx={SubstanceCreatorModal}
                            pluginSettings={props.plugin}
                            setViewState={(vs) => setViewState(vs)}
                            popOverReport={{
                                items: props.dataFromPluginGridSelectorInput ? selectedItems : [],
                                onItemsChange: handleItemsChange
                            }}
                        />
                    </ToolbarWrapper>
                    <GridWrapper cols={8}>
                        <Input
                            className="custom-input-withPrefix"
                            style={{ width: props.isModal ? 360 : 360 }}
                            value={searchValue}
                            placeholder={t("directory.substance.expressionInputPlaceholder")}
                            prefix={<SearchIcon />}
                            onPressEnter={handlePressEnter}
                            onChange={handleSearchChange}
                            onBlur={() => {
                                if (props.isModal) props.onFocusChanged?.(false);
                            }}
                            allowClear
                        />
                    </GridWrapper>
                </>
            }

            {
                isSubmitting ? <Spinner /> : <DefaultGrid
                    autoSelect
                    showQuantity
                    multipleSelect={props.multipleSelect}
                    selectedItems={selectedItems}
                    gridId={props.gridId}
                    data={data}
                    loadingStatus={loadingStatus}
                    setLoadingStatus={setLoadingStatus}
                    totalCount={totalCount}
                    filter={gridFilter}
                    dataProvider={substanceDataProvider}
                    plugin={props.plugin}
                    separator
                    singleDirectory
                    selectedItem={selectedItem}
                    onDoubleClick={(row) => props.onDoubleClick?.(row)}
                    onSelect={(row) => {
                        setSelectedItem(row)
                        props.onSelect?.(row)
                    }}
                    onMultipleSelect={(rows) => {
                        setSelectedItems(rows)
                        props?.onMultipleSelect?.(rows)
                    }}
                    onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                    onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                    onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                    onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                    onEnter={() => setViewState("edit")}
                    isSearchForMultyselect={props.isSearchForMultyselect}
                />
            }
        </PluginWrapper>
    )
}

export default SubstanceGrid